import { fromZonedTime } from "date-fns-tz";
import { RISE_FINAL_CLOSE_HOUR, RISE_TIMEZONE, RISE_UNLOCK_HOUR } from "./constants";
import { RISE_DAYS } from "./schedule";
import type { RiseDay, RiseDayPhase, RiseDayWithPhase } from "./types/ui-types";

/**
 * Turn a local wall-clock time in the campaign zone into an absolute instant.
 *
 * @param date - Local date, `YYYY-MM-DD`.
 * @param hour - Local hour, 0-23.
 * @returns The instant that wall-clock time happens.
 */
function atLocalHour(date: string, hour: number): Date {
  return fromZonedTime(`${date}T${String(hour).padStart(2, "0")}:00:00`, RISE_TIMEZONE);
}

/**
 * When a day's game opens and closes. A day closes the moment the next one opens (so Friday's
 * game runs through the weekend), and the last day closes at the final close hour on its date.
 *
 * @param day - The countdown day.
 * @returns Open (inclusive) and close (exclusive) instants.
 */
export function getDayWindow(day: RiseDay): { opensAt: Date; closesAt: Date } {
  const opensAt = atLocalHour(day.date, RISE_UNLOCK_HOUR);
  const next = RISE_DAYS.find((d) => d.day === day.day + 1);
  const closesAt = next ? atLocalHour(next.date, RISE_UNLOCK_HOUR) : atLocalHour(day.date, RISE_FINAL_CLOSE_HOUR);
  return { opensAt, closesAt };
}

/**
 * Where a day sits relative to a moment.
 *
 * @param day - The countdown day.
 * @param now - The moment to compare against.
 * @returns "upcoming" before it opens, "open" while it's scoreable, "closed" after.
 */
export function getDayPhase(day: RiseDay, now: Date): RiseDayPhase {
  const { opensAt, closesAt } = getDayWindow(day);
  if (now < opensAt) return "upcoming";
  if (now >= closesAt) return "closed";
  return "open";
}

/**
 * Every countdown day with its phase and window, for rendering.
 *
 * @param now - The moment to compare against.
 * @returns All eight days, in order.
 */
export function getDaysWithPhase(now: Date): RiseDayWithPhase[] {
  return RISE_DAYS.map((day) => {
    const { opensAt, closesAt } = getDayWindow(day);
    return { ...day, phase: getDayPhase(day, now), opensAt: opensAt.toISOString(), closesAt: closesAt.toISOString() };
  });
}

/**
 * The day that's open right now, if any.
 *
 * @param now - The moment to compare against.
 * @returns The open day, or undefined before the countdown starts and after it ends.
 */
export function getOpenDay(now: Date): RiseDay | undefined {
  return RISE_DAYS.find((day) => getDayPhase(day, now) === "open");
}

/**
 * The last day whose window has started, open or closed. Streaks and standings are computed
 * through this day.
 *
 * @param now - The moment to compare against.
 * @returns The day number, or 0 before the countdown starts.
 */
export function getLatestStartedDay(now: Date): number {
  const started = RISE_DAYS.filter((day) => getDayPhase(day, now) !== "upcoming");
  return started.length === 0 ? 0 : started[started.length - 1].day;
}
